/**
 * Verify Email Page
 *
 * Handles email verification from the link sent to users.
 * Extracts token from URL and displays form to complete admin setup.
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useVerifyEmail } from "../hooks/useVerifyEmail";
import { verificationFormDataSchema, type VerificationFormData } from "../../../lib/validators/tenant";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Alert } from "../../../components/ui/Alert";
import { FormField } from "../../../components/forms/FormField";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

export const VerifyTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"form" | "success" | "error">("form");
  const [errorMessage, setErrorMessage] = useState("");

  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationFormDataSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
    },
  });

  const { mutate: verifyEmail, isPending } = useVerifyEmail({
    onSuccess: () => {
      setStatus("success");
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    },
    onError: (error) => {
      setStatus("error");
      const message = error.message || "Verification failed";
      if (message.toLowerCase().includes("expired")) {
        setErrorMessage("This verification link has expired. Please request a new one.");
      } else if (message.toLowerCase().includes("invalid")) {
        setErrorMessage("This verification link is invalid. Please check your email for the correct link.");
      } else if (message.toLowerCase().includes("already")) {
        setErrorMessage("This account has already been verified. Please try logging in.");
      } else {
        setErrorMessage("Verification failed. Please try again or contact support.");
      }
    },
  });

  const onSubmit = (data: VerificationFormData) => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token provided. Please check your email for the verification link.");
      return;
    }

    setErrorMessage("");
    verifyEmail({
      verificationToken: token,
      ...data,
    });
  };

  const handleResendVerification = () => {
    // TODO: Implement resend verification
    alert("Resend verification feature coming soon!");
  };

  // Check if token is missing
  if (!token && status === "form") {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
              <XCircleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Invalid Link</h2>
            <div className="mt-4">
              <Alert variant="error">No verification token provided. Please check your email for the verification link.</Alert>
            </div>
          </div>

          <div className="text-center">
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-accent-100">
              <CheckCircleIcon className="h-10 w-10 text-accent-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Account Created!</h2>
            <div className="mt-4 space-y-4">
              <p style={{ color: 'var(--color-text-secondary)' }}>Your email has been verified and your admin account has been created successfully.</p>
              <Alert variant="success">Redirecting you to login in 3 seconds...</Alert>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Button variant="primary" onClick={() => navigate("/login")} fullWidth size="lg">
              Go to Sign In Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
              <XCircleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Verification Failed</h2>
            <div className="mt-4">
              <Alert variant="error">{errorMessage}</Alert>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Button variant="secondary" onClick={handleResendVerification} fullWidth>
              Resend Verification Email
            </Button>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              or{" "}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                go back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Complete Your Registration</h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Set up your admin account to get started with Ketrics</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {errorMessage && (
            <Alert variant="error" dismissible onDismiss={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          )}

          <div className="card shadow-card rounded-lg p-6 space-y-6">
            <FormField label="First Name" required error={errors.firstName?.message}>
              <Input {...register("firstName")} type="text" placeholder="John" error={errors.firstName?.message} disabled={isPending} autoFocus />
            </FormField>

            <FormField label="Last Name" required error={errors.lastName?.message}>
              <Input {...register("lastName")} type="text" placeholder="Doe" error={errors.lastName?.message} disabled={isPending} />
            </FormField>

            <FormField
              label="Password"
              required
              error={errors.password?.message}
              helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
            >
              <Input {...register("password")} type="password" placeholder="--------" error={errors.password?.message} disabled={isPending} />
            </FormField>
          </div>

          <Button type="submit" variant="primary" size="lg" fullWidth loading={isPending} disabled={isPending}>
            Complete Setup
          </Button>

          <p className="text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            By completing setup, you agree to our{" "}
            <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
              Privacy Policy
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default VerifyTenantPage;
